import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  Alert,
  Avatar,
  Button,
  Checkbox,
  Empty,
  Input,
  Modal,
  Radio,
  Result,
  Skeleton,
  Steps,
  Tag
} from 'antd';
import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import BridgeMarks from '../BridgeMarks';
import WizardWrapper from './styles';
import { IMPORT_DISMISSED_KEY } from './constants';
import {
  useGetSafetyloopDirectory,
  useGetSafetyloopImportStatus,
  useImportSafetyloopGroups
} from '../../services/SafetyloopImport/hooks';

// Post-sign-in wizard for SafetyLoop-bridged companies, in three steps: pick one
// SafetyLoop group, choose which of its people come along, then review what's
// coming over before the group becomes a ProjectLoop project. The API decides
// eligibility (linked admin, no active projects); the wizard pops whenever that
// holds, until skipped for this tab.

const STEP_ITEMS = [{ title: 'Group' }, { title: 'People' }, { title: "What's coming over" }];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SEARCH_THRESHOLD = 8;
const REVIEW_NAME_LIMIT = 12;

const readDismissed = () => {
  try {
    return sessionStorage.getItem(IMPORT_DISMISSED_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const writeDismissed = () => {
  try {
    sessionStorage.setItem(IMPORT_DISMISSED_KEY, '1');
  } catch (e) {
    // Storage blocked; closing still hides the wizard until the next reload.
  }
};

const fullName = (person) =>
  `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.email || 'Unnamed';

const initials = (person) =>
  fullName(person)
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const plural = (count, word, many = `${word}s`) => `${count} ${count === 1 ? word : many}`;

const groupLocation = (group) => [group.city, group.province_or_state].filter(Boolean).join(', ');

const groupAddress = (group) =>
  [group.address, group.city, group.province_or_state, group.postal_code]
    .filter(Boolean)
    .join(', ');

const apiError = (error, fallback) => error?.response?.data?.error || fallback;

const NameList = ({ people }) => {
  const shown = people.slice(0, REVIEW_NAME_LIMIT);
  const more = people.length - shown.length;
  return (
    <div className="wiz-name-list">
      {shown.map((person) => (
        <Tag key={person.id}>{fullName(person)}</Tag>
      ))}
      {more > 0 && <span className="wiz-more">+{more} more</span>}
    </div>
  );
};

const ImportWizard = ({ onClose }) => {
  const navigate = useNavigate();
  const companyId = Cookies.get('company_id');
  const [step, setStep] = useState(0);
  const [search, setSearch] = useState('');
  const [groupId, setGroupId] = useState(null);
  // Track who was unticked rather than who is ticked, so everyone in the group
  // comes along by default.
  const [excludedIds, setExcludedIds] = useState(() => new Set());
  const directory = useGetSafetyloopDirectory(true);
  const importer = useImportSafetyloopGroups(companyId);

  const groups = useMemo(() => directory.data?.groups || [], [directory.data]);
  const group = useMemo(() => groups.find((item) => item.id === groupId), [groups, groupId]);

  const visibleGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups.filter((item) =>
      [item.name, item.job_code, groupLocation(item)].some((value) =>
        (value || '').toLowerCase().includes(term)
      )
    );
  }, [groups, search]);

  const people = useMemo(() => {
    if (!group) return [];
    const usersById = new Map((directory.data?.users || []).map((user) => [user.id, user]));
    const managers = new Set(group.manager_ids || []);
    return (group.member_ids || [])
      .map((id) => usersById.get(id))
      .filter(Boolean)
      .map((person) => ({
        ...person,
        manages: managers.has(person.id),
        importable: EMAIL_PATTERN.test(person.email || '')
      }))
      .sort((a, b) =>
        `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)
      );
  }, [directory.data, group]);

  const importablePeople = people.filter((person) => person.importable);
  const comingPeople = importablePeople.filter((person) => !excludedIds.has(person.id));
  const comingManagers = comingPeople.filter((person) => person.manages);
  const comingWorkers = comingPeople.filter((person) => !person.manages);
  const leftBehind = people.length - comingPeople.length;

  const chooseGroup = (id) => {
    if (id === groupId) return;
    setGroupId(id);
    setExcludedIds(new Set());
  };

  const togglePerson = (id) =>
    setExcludedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const setAllPeople = (included) =>
    setExcludedIds(included ? new Set() : new Set(importablePeople.map((person) => person.id)));

  const runImport = () => {
    setStep(3);
    importer.mutate(
      { group_ids: [groupId], user_ids: comingPeople.map((person) => person.id) },
      { onError: () => setStep(2) }
    );
  };

  const openProject = () => {
    const [project] = importer.data?.projects || [];
    localStorage.setItem(
      'company',
      JSON.stringify({ name: Cookies.get('company_name'), unique_reference_id: companyId })
    );
    onClose();
    if (project) navigate(`/projects/${project.id}`);
    else navigate(`/projects-list/${companyId}`);
  };

  const renderGroups = () => {
    if (directory.isLoading) {
      return <Skeleton active paragraph={{ rows: 6 }} style={{ paddingTop: 16 }} />;
    }
    if (directory.isError) {
      return (
        <Result
          status="warning"
          title="We couldn't load your SafetyLoop groups"
          subTitle={apiError(directory.error, 'Please try again in a moment.')}
          extra={
            <Button type="primary" onClick={() => directory.refetch()}>
              Try again
            </Button>
          }
        />
      );
    }
    if (!groups.length) {
      return (
        <div className="wiz-center">
          <Empty description="There are no SafetyLoop groups you can bring over yet." />
        </div>
      );
    }
    return (
      <>
        <div className="wiz-toolbar">
          {groups.length > SEARCH_THRESHOLD ? (
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search groups"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          ) : (
            <span>Choose the group to start your first project from.</span>
          )}
        </div>
        <Radio.Group
          className="wiz-list"
          value={groupId}
          onChange={(event) => chooseGroup(event.target.value)}>
          {visibleGroups.map((item) => {
            const location = groupLocation(item);
            return (
              <Radio key={item.id} className="wiz-row" value={item.id}>
                <div className="wiz-row-main">
                  <div className="wiz-row-title">
                    <span>{item.name}</span>
                    {item.kind === 'Global Project' && <Tag color="blue">Global Project</Tag>}
                  </div>
                  <div className="wiz-row-meta">
                    {[
                      location,
                      item.job_code,
                      plural((item.member_ids || []).length, 'person', 'people')
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
              </Radio>
            );
          })}
          {!visibleGroups.length && <Empty description="No groups match your search." />}
        </Radio.Group>
      </>
    );
  };

  const renderPeople = () => {
    if (!people.length) {
      return (
        <div className="wiz-center">
          <Empty description="This group has no people to bring over. You can still import it as an empty project." />
        </div>
      );
    }
    const allIncluded = comingPeople.length === importablePeople.length;
    return (
      <>
        <div className="wiz-toolbar">
          <span>
            {comingPeople.length} of {plural(people.length, 'person', 'people')} selected
          </span>
          <Button type="link" onClick={() => setAllPeople(!allIncluded)}>
            {allIncluded ? 'Clear all' : 'Select all'}
          </Button>
        </div>
        <div className="wiz-list">
          {people.map((person) => (
            <Checkbox
              key={person.id}
              className="wiz-row"
              checked={person.importable && !excludedIds.has(person.id)}
              disabled={!person.importable}
              onChange={() => togglePerson(person.id)}>
              <Avatar className="wiz-avatar">{initials(person)}</Avatar>
              <div className="wiz-row-main">
                <div className="wiz-row-title">
                  <span>{fullName(person)}</span>
                  {person.manages && <Tag color="orange">Project manager</Tag>}
                </div>
                <div className="wiz-row-meta">
                  {person.importable ? (
                    [person.email, person.role].filter(Boolean).join(' · ')
                  ) : (
                    <em>No email in SafetyLoop, so they can&apos;t be added</em>
                  )}
                </div>
              </div>
            </Checkbox>
          ))}
        </div>
      </>
    );
  };

  const renderReview = () => {
    const details = [
      ['Job code', group.job_code],
      ['Address', groupAddress(group)],
      ['Description', group.description]
    ].filter(([, value]) => value);
    return (
      <div className="wiz-review">
        {importer.isError && (
          <Alert
            type="error"
            showIcon
            message={apiError(importer.error, 'The import failed. Please try again.')}
          />
        )}
        <section className="wiz-review-section">
          <h4>Project</h4>
          <div className="wiz-review-card">
            <div className="wiz-row-title">
              <span>{group.name}</span>
              {group.kind === 'Global Project' && <Tag color="blue">Global Project</Tag>}
            </div>
            {details.length > 0 && (
              <dl className="wiz-details">
                {details.map(([label, value]) => (
                  <React.Fragment key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            )}
          </div>
        </section>
        <section className="wiz-review-section">
          <h4>People</h4>
          {comingPeople.length ? (
            <>
              {comingManagers.length > 0 && (
                <div className="wiz-review-group">
                  <span className="wiz-review-label">
                    {plural(comingManagers.length, 'project manager')}
                  </span>
                  <NameList people={comingManagers} />
                </div>
              )}
              {comingWorkers.length > 0 && (
                <div className="wiz-review-group">
                  <span className="wiz-review-label">
                    {plural(comingWorkers.length, 'field worker')}
                  </span>
                  <NameList people={comingWorkers} />
                </div>
              )}
            </>
          ) : (
            <p className="wiz-muted">Nobody yet. You can invite people to the project later.</p>
          )}
          {leftBehind > 0 && (
            <p className="wiz-muted">
              {plural(leftBehind, 'person', 'people')} from this group won&apos;t come over.
            </p>
          )}
        </section>
        <Alert
          type="info"
          showIcon
          message="No invitation emails are sent. People sign in from SafetyLoop, as you just did. The project starts today on a one-year schedule; you can change the dates, stage and type in project settings."
        />
      </div>
    );
  };

  const renderResult = () => {
    if (importer.isLoading || !importer.data) {
      return (
        <div className="wiz-center">
          <BridgeMarks size={48} animated />
          <h3>Creating your project…</h3>
          <p>
            Bringing over {group.name} and {plural(comingPeople.length, 'person', 'people')}.
          </p>
        </div>
      );
    }
    const { projects = [], users_created: created = 0, users_linked: linked = 0 } = importer.data;
    const skipped = importer.data.skipped || [];
    return (
      <div className="wiz-center">
        <span className="wiz-done-icon">
          <CheckOutlined />
        </span>
        <h3>{projects.length ? 'Your project is ready' : 'Nothing was imported'}</h3>
        <p>
          {projects.length ? projects[0].name : group.name} · {plural(created, 'person', 'people')}{' '}
          added
          {linked ? ` · ${linked} already in ProjectLoop` : ''}
        </p>
        {skipped.length > 0 && (
          <Alert
            className="wiz-skipped"
            type="warning"
            showIcon
            message={`${skipped.length} couldn't be brought over`}
            description={
              <ul>
                {skipped.map((item) => (
                  <li key={`${item.kind}-${item.name}`}>
                    {item.name}: {item.reason}
                  </li>
                ))}
              </ul>
            }
          />
        )}
      </div>
    );
  };

  const BODY = [renderGroups, renderPeople, renderReview, renderResult];
  const importing = step === 3 && !importer.data;
  const done = step === 3 && !!importer.data;

  return (
    <Modal
      open
      centered
      destroyOnClose
      width={720}
      footer={null}
      maskClosable={false}
      closable={!importing}
      keyboard={!importing}
      onCancel={onClose}
      bodyStyle={{ padding: 0 }}>
      <WizardWrapper>
        <header className="wiz-header">
          <BridgeMarks size={32} />
          <div>
            <h2 className="wiz-title">Bring a SafetyLoop group into ProjectLoop</h2>
            <p className="wiz-subtitle">
              Pick one group to start with. It becomes your first project, with the people you
              choose as members.
            </p>
          </div>
        </header>
        {step < 3 && <Steps className="wiz-steps" size="small" current={step} items={STEP_ITEMS} />}
        <div className="wiz-body">{BODY[step]()}</div>
        <footer className="wiz-footer">
          {done ? (
            <span />
          ) : (
            <Button type="link" disabled={importing} onClick={onClose} style={{ paddingLeft: 0 }}>
              Skip for now
            </Button>
          )}
          <div className="wiz-footer-actions">
            {step > 0 && step < 3 && <Button onClick={() => setStep(step - 1)}>Back</Button>}
            {step === 0 && (
              <Button type="primary" disabled={!group} onClick={() => setStep(1)}>
                Next
              </Button>
            )}
            {step === 1 && (
              <Button type="primary" onClick={() => setStep(2)}>
                Next
              </Button>
            )}
            {step === 2 && (
              <Button type="primary" onClick={runImport}>
                Create project
              </Button>
            )}
            {done && (
              <>
                <Button onClick={onClose}>Close</Button>
                {importer.data.projects?.length > 0 && (
                  <Button type="primary" onClick={openProject}>
                    Open project
                  </Button>
                )}
              </>
            )}
          </div>
        </footer>
      </WizardWrapper>
    </Modal>
  );
};

const SafetyloopImport = () => {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [open, setOpen] = useState(false);
  // /sso is about to hard-reload into the app; ask once it has.
  const { data: status } = useGetSafetyloopImportStatus(!dismissed && pathname !== '/sso');

  useEffect(() => {
    if (status?.eligible && !dismissed && pathname !== '/sso') setOpen(true);
  }, [status, dismissed, pathname]);

  const close = () => {
    writeDismissed();
    setDismissed(true);
    setOpen(false);
  };

  if (!open) return null;
  return <ImportWizard onClose={close} />;
};

export default SafetyloopImport;
